/** Brainfish Platform format: parse "=widthxheight" from image title. */
const imageSizeRegex = /\s?=(\d*?)x(\d*)$/;
const MAX_DIMENSION = 2048;

/** True when value is neither `null` nor `undefined` (strict; avoids loose `!= null`). */
export function isPresent<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

function parseBoundedDimension(value: string | undefined): number | undefined {
  if (!isPresent(value) || value === '') return undefined;
  const n = parseInt(value, 10);
  if (Number.isNaN(n) || n < 1 || n > MAX_DIMENSION) return undefined;

  return n;
}

export function parseSizeFromTitle(title: string | undefined): {
  width?: number;
  height?: number;
  cleanTitle: string;
} {
  const match = title?.match(imageSizeRegex);
  if (!match) return { cleanTitle: title ?? '' };
  const width = parseBoundedDimension(match[1]);
  const height = parseBoundedDimension(match[2]);
  if (width === undefined && height === undefined) return { cleanTitle: title ?? '' };

  return {
    width,
    height,
    cleanTitle: title ? title.replace(imageSizeRegex, '').trim() : '',
  };
}

/** Reconstruct Platform dimension suffix for markdown image title. */
export function formatSizeTitle(width?: number, height?: number): string {
  if (!isPresent(width) && !isPresent(height)) return '';
  const w = isPresent(width) ? String(width) : '';
  const h = isPresent(height) ? String(height) : '';

  return `=${w}x${h}`;
}

/**
 * Parse image block attributes from markdown alt and title.
 * Supports Platform format (![caption](url "=WxH")) and legacy Milkdown format (![ratio](url "caption")).
 */
export function parseImageBlock(
  alt: string | undefined,
  title: string | undefined,
  nodeWidth?: number,
  nodeHeight?: number,
): { ratio: number; caption: string; width?: number; height?: number } {
  const { width: titleWidth, height: titleHeight, cleanTitle } = parseSizeFromTitle(title);
  const width = titleWidth ?? nodeWidth;
  const height = titleHeight ?? nodeHeight;
  const hasPlatformDimensions = width !== undefined || height !== undefined;

  let ratio = 1;
  let caption = '';

  if (hasPlatformDimensions) {
    caption = alt || cleanTitle;
  } else {
    const parsedRatio = Number(alt || 1);
    if (!Number.isNaN(parsedRatio) && parsedRatio !== 0) {
      ratio = parsedRatio;
      caption = cleanTitle;
    } else {
      caption = alt || cleanTitle;
    }
  }

  return { ratio, caption, width, height };
}
